"use client";

import { c as _c } from "react/compiler-runtime";
import { ComingSoon } from "@/components/common/coming-soon";
import { ActionGuard } from "@/components/guards/action-guard";
import { Button } from "@/components/rac/button";
import { Card } from "@/components/rac/card";
import { Icon } from "@/components/rac/icon";
import { syncStorage } from "@/lib/storage";
import { trackSellerListing } from "@/utils/analytics/track-seller-listing";
import { CheckCircle, ShieldCheck, Star, TrendingUp } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import CallbackRequestModal from "../sup/callback-request-modal";
import { useCallbackRequest } from "./use-callback-request";
const stats = [{
  value: "4 Lakh+",
  label: "Genuine Buyers"
}, {
  value: "4.6/5",
  label: "Rating",
  suffix: "*"
}, {
  value: "30k+",
  label: "Sellers Trusted Us"
}, {
  value: "₹2500Cr+",
  label: "Worth Homes Sold"
}];
const benefits = [{
  icon: "💰",
  title: "No Upfront/Hidden Charges",
  description: "Pay only when your property is sold. No hidden fees or upfront costs."
}, {
  icon: "📱",
  title: "All-Round Marketing",
  description: "Premium listing across multiple platforms with professional photography."
}, {
  icon: "✅",
  title: "Genuine Buyers",
  description: "Pre-verified buyers with serious purchase intent and financial capability."
}, {
  icon: "🤝",
  title: "End-to-End Support",
  description: "Complete assistance from listing to deal closure with legal documentation."
}, {
  icon: "👨‍💼",
  title: "Dedicated Relationship Manager",
  description: "Personal point of contact throughout your property selling journey."
}];
const features = ["Professional property photography", "Premium listing on multiple portals", "Social media marketing campaigns", "Buyer screening and verification", "Price negotiation assistance", "Legal documentation support", "Loan facilitation services", "Property valuation report"];
export function SellContent() {
  const $ = _c(49);
  const [showValuation, setShowValuation] = useState(false);
  const {
    isAuthenticated: callbackIsAuthenticated,
    callbackModalOpen,
    userPhoneNumbers,
    openCallbackModal,
    closeCallbackModal
  } = useCallbackRequest();
  const router = useRouter();
  const pathname = usePathname();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp, t0);
  let t1;
  if ($[1] !== pathname || $[2] !== router) {
    t1 = () => {
      const stored = syncStorage.getItem("settlin-sell-form-state");
      const parsed = stored ? JSON.parse(stored) : null;
      const url = `${pathname}?${parsed?.rawUnitId ? `rawUnitId=${parsed.rawUnitId}&` : ""}process=profile`;
      router.push(url);
    };
    $[1] = pathname;
    $[2] = router;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleListClick = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary-100/20 via-transparent to-transparent" />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-2 text-sm font-medium text-primary-700"><Icon icon={Star} className="h-4 w-4" /><span>Trusted by 30,000+ sellers</span></div>;
    t4 = <h1 className="mb-6 text-4xl font-bold tracking-tight text-neutral-900 md:text-6xl lg:text-7xl">Sell Your House in Bengaluru quickly</h1>;
    t5 = <p className="mb-8 text-xl text-neutral-600 md:text-2xl">Sell your property with Bengaluru's top resale experts</p>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex items-center gap-2"><Icon icon={CheckCircle} className="h-5 w-5 text-green-600" /><span>No Upfront Charges</span></div>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex items-center gap-2"><Icon icon={TrendingUp} className="h-5 w-5 text-blue-600" /><span>Premium Marketing</span></div>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="mb-10 flex flex-wrap items-center justify-center gap-6 text-sm font-medium text-neutral-700 md:text-base">{t6}{t7}<div className="flex items-center gap-2"><Icon icon={ShieldCheck} className="h-5 w-5 text-purple-600" /><span>End-to-end Assistance</span></div></div>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== handleListClick) {
    t9 = <ActionGuard actionId="list_property"><Button size="lg" className="bg-primary-600 px-8 py-4 text-lg font-semibold hover:bg-primary-700" onPress={handleListClick}>List Your Property Now</Button></ActionGuard>;
    $[11] = handleListClick;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== showValuation) {
    t10 = showValuation && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"><div className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full relative"><button className="absolute top-2 right-2 text-2xl text-neutral-400 hover:text-neutral-600" onClick={() => setShowValuation(false)} aria-label="Close">×</button><ComingSoon message="Valuation feature will be available soon." className="bg-blue-50 rounded-lg" /></div></div>;
    $[13] = showValuation;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t9) {
    t11 = <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-white to-secondary-50 py-16 md:py-24">{t2}<div className="container relative mx-auto px-4"><div className="mx-auto max-w-4xl text-center">{t3}{t4}{t5}{t8}<div className="flex flex-col gap-4 sm:flex-row sm:justify-center">{t9}{t10}</div></div></div></section>;
    $[15] = t10;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <section className="py-16 bg-white"><div className="container mx-auto px-4"><div className="grid grid-cols-2 gap-8 md:grid-cols-4">{stats.map(_temp2)}</div></div></section>;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="mx-auto max-w-3xl text-center mb-16"><h2 className="mb-4 text-3xl font-bold text-neutral-900 md:text-4xl">Why Choose Settlin?</h2><p className="text-lg text-neutral-600">We make property selling simple, transparent, and profitable for you</p></div>;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <section className="py-20 bg-neutral-50"><div className="container mx-auto px-4">{t13}<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">{benefits.map(_temp3)}</div></div></section>;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  let t16;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <h2 className="mb-4 text-3xl font-bold text-neutral-900 md:text-4xl">Simple, Transparent Pricing</h2>;
    t16 = <p className="mb-12 text-lg text-neutral-600">No hidden charges. Pay only when your property is sold.</p>;
    $[21] = t15;
    $[22] = t16;
  } else {
    t15 = $[21];
    t16 = $[22];
  }
  let t17;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="mb-6"><div className="text-5xl font-bold text-primary-600 md:text-6xl">1.9%</div><div className="text-xl text-neutral-700 mt-2">Flat commission of sale value</div></div>;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="p-4 bg-white rounded-lg border"><div className="font-semibold text-neutral-900 mb-1">Maximum Commission</div><div className="text-2xl font-bold text-primary-600">₹4 Lakh</div></div>;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="grid gap-4 md:grid-cols-2 text-left">{t18}<div className="p-4 bg-white rounded-lg border"><div className="font-semibold text-neutral-900 mb-1">Minimum Commission</div><div className="text-2xl font-bold text-primary-600">₹1 Lakh</div></div></div>;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== handleListClick) {
    t20 = <section className="py-20 bg-white"><div className="container mx-auto px-4"><div className="mx-auto max-w-4xl text-center">{t15}{t16}<Card className="p-8 md:p-12 bg-gradient-to-br from-primary-50 to-secondary-50 border-2 border-primary-200">{t17}{t19}<div className="mt-8"><ActionGuard actionId="list_property"><Button size="lg" className="bg-primary-600 px-8 py-4 text-lg font-semibold hover:bg-primary-700" onPress={handleListClick}>Start Selling Today</Button></ActionGuard></div></Card></div></div></section>;
    $[26] = handleListClick;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="mx-auto max-w-3xl text-center mb-16"><h2 className="mb-4 text-3xl font-bold text-neutral-900 md:text-4xl">What You Get</h2><p className="text-lg text-neutral-600">Comprehensive services to ensure your property sells at the best price</p></div>;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  let t22;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <section className="py-20 bg-neutral-50"><div className="container mx-auto px-4">{t21}<div className="mx-auto max-w-4xl"><div className="grid gap-4 md:grid-cols-2">{features.map(_temp4)}</div></div></div></section>;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  let t23;
  let t24;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <h2 className="mb-6 text-3xl font-bold text-white md:text-4xl">Ready to Sell Your Property?</h2>;
    t24 = <p className="mb-8 text-xl text-primary-100">Join thousands of satisfied sellers who chose Settlin for their property sale</p>;
    $[30] = t23;
    $[31] = t24;
  } else {
    t23 = $[30];
    t24 = $[31];
  }
  let t25;
  if ($[32] !== handleListClick) {
    t25 = <ActionGuard actionId="list_property"><Button size="lg" variant="outline" onPress={handleListClick} className="border-white bg-white text-primary-600 px-8 py-4 text-lg font-semibold hover:bg-primary-50">List Your Property</Button></ActionGuard>;
    $[32] = handleListClick;
    $[33] = t25;
  } else {
    t25 = $[33];
  }
  let t26;
  if ($[34] !== openCallbackModal) {
    t26 = <Button size="lg" variant="ghost" className="text-white border-white hover:bg-primary-800 px-8 py-4 text-lg font-semibold" onPress={openCallbackModal}>Talk to Expert</Button>;
    $[34] = openCallbackModal;
    $[35] = t26;
  } else {
    t26 = $[35];
  }
  let t27;
  if ($[36] !== t25 || $[37] !== t26) {
    t27 = <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700"><div className="container mx-auto px-4 text-center"><div className="mx-auto max-w-3xl">{t23}{t24}<div className="flex flex-col gap-4 sm:flex-row sm:justify-center">{t25}{t26}</div></div></div></section>;
    $[36] = t25;
    $[37] = t26;
    $[38] = t27;
  } else {
    t27 = $[38];
  }
  let t28;
  if ($[39] !== callbackIsAuthenticated || $[40] !== callbackModalOpen || $[41] !== closeCallbackModal || $[42] !== userPhoneNumbers) {
    t28 = <CallbackRequestModal open={callbackModalOpen} onClose={closeCallbackModal} isAuthenticated={callbackIsAuthenticated} userPhoneNumbers={userPhoneNumbers} />;
    $[39] = callbackIsAuthenticated;
    $[40] = callbackModalOpen;
    $[41] = closeCallbackModal;
    $[42] = userPhoneNumbers;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  let t29;
  if ($[44] !== t11 || $[45] !== t20 || $[46] !== t27 || $[47] !== t28) {
    t29 = <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white">{t11}{t12}{t14}{t20}{t22}{t27}{t28}</div>;
    $[44] = t11;
    $[45] = t20;
    $[46] = t27;
    $[47] = t28;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  return t29;
}
function _temp4(feature, index_1) {
  return <div key={index_1} className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm"><Icon icon={CheckCircle} className="h-5 w-5 text-green-600 flex-shrink-0" /><span className="text-neutral-700">{feature}</span></div>;
}
function _temp3(benefit, index_0) {
  return <Card key={index_0} className="group p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"><div className="mb-4 text-4xl">{benefit.icon}</div><h3 className="mb-3 text-xl font-semibold text-neutral-900">{benefit.title}</h3><p className="text-neutral-600 leading-relaxed">{benefit.description}</p></Card>;
}
function _temp2(stat, index) {
  return <div key={index} className="text-center"><div className="mb-2 text-3xl font-bold text-primary-600 md:text-4xl">{stat.value}{stat.suffix && <span className="text-2xl text-neutral-500">{stat.suffix}</span>}</div><div className="text-sm font-medium text-neutral-600 md:text-base">{stat.label}</div></div>;
}
function _temp() {
  trackSellerListing({
    action: "view"
  });
}